import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { classNames } from 'primereact/utils';
import { createPortal } from 'react-dom';
import { Button, CheckCircleIcon, globalToastService } from '@repo/atomic-components';
import {
  BundleType,
  type BundleTypeKeys,
  makeSelectBundlePageIdByPageIndex,
  makeSelectBundlePageIndexByPageId,
  makeSelectFullBundlePageIndexByPageId,
  makeSelectLightBundlePageIndexByPageId,
  selectBundleMetadataByStoreKey,
  selectBundleSections,
  selectPageInfoCache,
  selectSharedDataActiveBundleType,
  selectSharedDataBundleId,
  selectViewportPageIndexInStore,
  setBundleRetrieveBasePageId,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { useLazyGetBundleDuplicatesQuery } from '@/api/bundle/getBundleDuplicates';
import { useLazyGetBundleSimilarsQuery } from '@/api/bundle/getBundleSimilars';
import { BundleMetadataType, type BundleDuplicateItem, type BundleSimilarItem } from '@/api';
import { useViewportPageIndexContext } from '../../hooks';
import {
  BROADCAST_FORCE_CATEGORY_LIST_REFRESH,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE,
  BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE,
  useFletchersBroadcast,
} from '@/broadcast';
import { AppRoutes, AppRoutesSearchParams } from '@/constants';
import { openNewTab } from '@/utils';
import { RetrievePagesDialog } from '../retrievePagesDialog';

interface RelatedRow {
  key: string;
  kind: 'duplicate' | 'similar';
  pageId: string;
  bundleType: 'LIGHT' | 'FULL';
  providerName: string;
  categoryName: string;
}

/**
 * Keep the first entry per (bundleType, pageId), preserving input order.
 * Both duplicate/similar lists are bundle-wide, so the filter+findIndex form
 * this replaces was quadratic — ~10s per render on a 28k-page bundle.
 */
const dedupeByBundleTypeAndPageId = <T extends { pageId: string; bundleType: string }>(items: T[]): T[] => {
  const seenPageIdsByBundleType = new Map<string, Set<string>>();
  return items.filter((item) => {
    let seenPageIds = seenPageIdsByBundleType.get(item.bundleType);
    if (!seenPageIds) {
      seenPageIds = new Set<string>();
      seenPageIdsByBundleType.set(item.bundleType, seenPageIds);
    }
    if (seenPageIds.has(item.pageId)) return false;
    seenPageIds.add(item.pageId);
    return true;
  });
};

const tooltipButtonBase = '!h-[32px] !w-[32px] !p-0 justify-center hover:!bg-gray-50';
const hideLabel = { label: { className: '!hidden' } };
// Both buttons in this panel sit on the shared PDF rail (via
// ZoomAndNavigatePdfPanel's bottomSlot) — the `zoom-rail-tooltip` class
// opts them into the same 32px-tall tooltip rule as the rail's own
// buttons (scoped in mermaidd's index.css) so labels baseline-align.
const railTooltipOptions = {
  position: 'left' as const,
  showDelay: 200,
  at: 'left-8 top',
  my: 'right top',
  className: 'zoom-rail-tooltip',
};

/**
 * Speedometer-style confidence indicator. The arc is drawn as a 3/4 sweep
 * from 7:30 (bottom-left) clockwise to 4:30 (bottom-right). The needle
 * points at the end of the filled portion.
 */
const ConfidenceDial: React.FC<{ level: 'high' | 'moderate' }> = ({ level }) => {
  const trackColor = 'var(--color-fletchers-green-50, #e1f5ee)';
  const fillColor =
    level === 'high' ? 'var(--color-fletchers-green-500, #195b53)' : 'var(--color-fletchers-green-300, #4e9277)';

  // Sweep goes from angle -225° (7:30) to +45° (4:30) — 270° total.
  const startAngleDeg = -225;
  const totalSweepDeg = 270;
  // Fraction of the arc that is "filled" for each level.
  const fillFraction = level === 'high' ? 1 : 0.72;

  // Convert polar (degrees, radius) to a point on our 16x16 dial centred at (8, 8).
  // Angle 0 = right; angles increase clockwise (SVG y-down).
  const cx = 8;
  const cy = 8;
  const r = 6;
  const polar = (angleDeg: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const start = polar(startAngleDeg);
  const end = polar(startAngleDeg + totalSweepDeg);
  // Track: full 270° arc, largeArcFlag=1 because sweep > 180°.
  const trackPath = `M ${start.x} ${start.y} A ${r} ${r} 0 1 1 ${end.x} ${end.y}`;

  // Filled arc: from start to the "needle" angle.
  const filledEndAngle = startAngleDeg + totalSweepDeg * fillFraction;
  const filledEnd = polar(filledEndAngle);
  const filledLargeArc = totalSweepDeg * fillFraction > 180 ? 1 : 0;
  const filledPath = `M ${start.x} ${start.y} A ${r} ${r} 0 ${filledLargeArc} 1 ${filledEnd.x} ${filledEnd.y}`;

  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d={trackPath} stroke={trackColor} strokeWidth="2" strokeLinecap="round" />
      <path d={filledPath} stroke={fillColor} strokeWidth="2" strokeLinecap="round" />
      {/* needle: from the centre to the end of the filled arc */}
      <line
        x1={cx}
        y1={cy}
        x2={filledEnd.x}
        y2={filledEnd.y}
        stroke={fillColor}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      <circle cx={cx} cy={cy} r="1.4" fill={fillColor} />
    </svg>
  );
};

interface RelatedPagesModalProps {
  buttonRect: DOMRect;
  totalCount: number;
  duplicateRows: RelatedRow[];
  similarRows: RelatedRow[];
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  renderRow: (row: RelatedRow) => React.ReactNode;
}

const RelatedPagesModal: React.FC<RelatedPagesModalProps> = ({
  buttonRect,
  totalCount,
  duplicateRows,
  similarRows,
  onMouseEnter,
  onMouseLeave,
  renderRow,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const MARGIN = 8;

  useLayoutEffect(() => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width || 320;
    const height = rect.height || 200;

    // Centre the modal vertically on the button, then clamp within the viewport.
    const buttonCentreY = buttonRect.top + buttonRect.height / 2;
    const idealTop = buttonCentreY - height / 2;
    const viewportH = window.innerHeight;
    const maxTop = Math.max(MARGIN, viewportH - height - MARGIN);
    const top = Math.min(Math.max(MARGIN, idealTop), maxTop);

    const left = buttonRect.left - MARGIN - width;

    setPos({ top, left });
  }, [buttonRect, duplicateRows.length, similarRows.length]);

  return createPortal(
    <div
      className="fixed z-[1000]"
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? -9999,
        visibility: pos ? 'visible' : 'hidden',
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        ref={cardRef}
        className={classNames(
          'border-fletchers-gray-110 flex max-h-[400px] w-[380px] flex-col overflow-hidden rounded-[8px] border bg-white',
          'shadow-[0px_4px_12px_0px_#0000001f]',
        )}
      >
        <div className="border-fletchers-gray-110 flex items-center border-b px-6 py-4">
          <div className="text-[14px] font-semibold text-black">
            {totalCount} duplicate {totalCount === 1 ? 'page' : 'pages'} detected
          </div>
        </div>
        <div className="max-h-[340px] overflow-y-auto px-6 pt-4 pb-4">
          {duplicateRows.length > 0 && (
            <div className="py-1">
              <div className="mb-1 flex items-center gap-2">
                <ConfidenceDial level="high" />
                <span className="text-fletchers-green-500 text-[14px] font-semibold">High confidence</span>
              </div>
              {duplicateRows.map(renderRow)}
            </div>
          )}
          {similarRows.length > 0 && (
            <div
              className={classNames('py-1', duplicateRows.length > 0 && 'border-fletchers-gray-110 mt-4 border-t pt-4')}
            >
              <div className="mb-1 flex items-center gap-2">
                <ConfidenceDial level="moderate" />
                <span className="text-fletchers-green-300 text-[14px] font-semibold">Moderate confidence</span>
              </div>
              {similarRows.map(renderRow)}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const RelatedPagesPanel: React.FC = () => {
  const urlParams = useParams();
  const dispatch = useAppDispatch();
  const { sendMessageWithResponse } = useFletchersBroadcast();

  const activeBundleType = useAppSelector(selectSharedDataActiveBundleType);
  const [isRetrievePagesDialogVisible, setIsRetrievePagesDialogVisible] = useState<boolean>(false);
  const bundleId = useAppSelector(selectSharedDataBundleId);
  const bundleSections = useAppSelector(selectBundleSections);
  const pageInfoCache = useAppSelector(selectPageInfoCache);
  const currentBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey());
  const otherBundleId = currentBundleMetadata?.otherBundleId;
  const lightBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey(BundleType.LIGHT));
  const fullBundleMetadata = useAppSelector(selectBundleMetadataByStoreKey(BundleType.FULL));

  const getBundlePageIdByPageIndex = useAppSelector(makeSelectBundlePageIdByPageIndex);
  const getBundlePageIndexByPageId = useAppSelector(makeSelectBundlePageIndexByPageId);
  const getFullBundlePageIndexByPageId = useAppSelector(makeSelectFullBundlePageIndexByPageId);
  const getLightBundlePageIndexByPageId = useAppSelector(makeSelectLightBundlePageIndexByPageId);

  const documentReviewPageContext = useViewportPageIndexContext();
  const viewportPageIndexInStore = useAppSelector(selectViewportPageIndexInStore);
  const pageId = getBundlePageIdByPageIndex(viewportPageIndexInStore);

  const [triggerGetDuplicates, { data: currentDuplicates }] = useLazyGetBundleDuplicatesQuery();
  const [triggerGetOtherDuplicates, { data: otherDuplicates }] = useLazyGetBundleDuplicatesQuery();
  const [triggerGetSimilars, { data: currentSimilars }] = useLazyGetBundleSimilarsQuery();
  const [triggerGetOtherSimilars, { data: otherSimilars }] = useLazyGetBundleSimilarsQuery();

  // Refetch similars/duplicates whenever the current bundle's sections tree
  // changes — that's what shifts when a user moves pages between categories
  // or renames a section, and the response contains providerName/categoryName
  // so a stale response would show out-of-date category labels here.
  useEffect(() => {
    if (bundleId) {
      triggerGetDuplicates({ bundleId });
      triggerGetSimilars({ bundleId });
    }
    if (otherBundleId) {
      triggerGetOtherDuplicates({ bundleId: otherBundleId });
      triggerGetOtherSimilars({ bundleId: otherBundleId });
    }
  }, [
    bundleId,
    otherBundleId,
    bundleSections,
    triggerGetDuplicates,
    triggerGetOtherDuplicates,
    triggerGetSimilars,
    triggerGetOtherSimilars,
  ]);

  // Also listen for the cross-tab "category list refreshed" broadcast so the
  // Light-Bundle tab (or the Full-Bundle tab, if the other window fires the
  // event) refetches when its counterpart moves/renames categories. Without
  // this, category labels would stay stale in the other window until reload.
  useEffect(() => {
    const handleCategoryRefresh = () => {
      if (bundleId) {
        triggerGetDuplicates({ bundleId });
        triggerGetSimilars({ bundleId });
      }
      if (otherBundleId) {
        triggerGetOtherDuplicates({ bundleId: otherBundleId });
        triggerGetOtherSimilars({ bundleId: otherBundleId });
      }
    };
    window.addEventListener(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, handleCategoryRefresh);
    return () => {
      window.removeEventListener(BROADCAST_FORCE_CATEGORY_LIST_REFRESH, handleCategoryRefresh);
    };
  }, [
    bundleId,
    otherBundleId,
    triggerGetDuplicates,
    triggerGetOtherDuplicates,
    triggerGetSimilars,
    triggerGetOtherSimilars,
  ]);

  const buttonRef = useRef<HTMLDivElement>(null);
  const [rowContextMenu, setRowContextMenu] = useState<{
    row: RelatedRow;
    isExtracted: boolean;
    top: number;
    left: number;
  } | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (openTimeoutRef.current) clearTimeout(openTimeoutRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    },
    [],
  );

  // Close the hover modal when the current page changes.
  useEffect(() => {
    setIsOpen(false);
  }, [pageId]);

  // Combining and deduping both bundles' lists depends only on the responses,
  // so it is kept out of the per-page memo below — otherwise every page change
  // (i.e. every thumbnail click) redid a whole bundle's worth of work.
  const { dedupedDuplicates, dedupedSims } = useMemo(() => {
    const allDuplicates: BundleDuplicateItem[] = [];
    if (currentDuplicates) allDuplicates.push(...currentDuplicates);
    if (otherDuplicates) allDuplicates.push(...otherDuplicates);

    const allSims: BundleSimilarItem[] = [];
    if (currentSimilars) allSims.push(...currentSimilars);
    if (otherSimilars) allSims.push(...otherSimilars);

    return {
      dedupedDuplicates: dedupeByBundleTypeAndPageId(allDuplicates),
      dedupedSims: dedupeByBundleTypeAndPageId(allSims),
    };
  }, [currentDuplicates, otherDuplicates, currentSimilars, otherSimilars]);

  // Filter to the items sharing a duplicate / similarity group with the current page.
  const relatedRows: { duplicates: RelatedRow[]; similars: RelatedRow[] } = useMemo(() => {
    if (!activeBundleType || !pageId) return { duplicates: [], similars: [] };

    const currentDupItem = dedupedDuplicates.find(
      (item) => item.bundleType.toLowerCase() === activeBundleType.toLowerCase() && item.pageId === pageId,
    );
    // Dedupe by underlying pageId — when a duplicate has been extracted into
    // the Light Bundle the same page appears twice in the merged list.
    const seenDupPageIds = new Set<string>();
    const dupRows: RelatedRow[] = currentDupItem
      ? dedupedDuplicates
          .filter((item) => item.duplicateGroupId === currentDupItem.duplicateGroupId && item.pageId !== pageId)
          .filter((item) => {
            if (seenDupPageIds.has(item.pageId)) return false;
            seenDupPageIds.add(item.pageId);
            return true;
          })
          .map((item, idx) => ({
            key: `dup-${item.pageId}-${item.bundleType}-${idx}`,
            kind: 'duplicate' as const,
            pageId: item.pageId,
            bundleType: item.bundleType,
            providerName: item.providerName,
            categoryName: item.categoryName,
          }))
      : [];

    const currentSimItem = dedupedSims.find(
      (item) => item.bundleType.toLowerCase() === activeBundleType.toLowerCase() && item.pageId === pageId,
    );
    // Suppress any "similar" row that we've already listed as a duplicate,
    // and dedupe by underlying pageId within the similars list too.
    const duplicatePageIds = new Set(dupRows.map((row) => row.pageId));
    const seenSimPageIds = new Set<string>();
    const simRows: RelatedRow[] = currentSimItem
      ? dedupedSims
          .filter(
            (item) =>
              item.similarityGroupId === currentSimItem.similarityGroupId &&
              item.pageId !== pageId &&
              !duplicatePageIds.has(item.pageId),
          )
          .filter((item) => {
            if (seenSimPageIds.has(item.pageId)) return false;
            seenSimPageIds.add(item.pageId);
            return true;
          })
          .map((item, idx) => ({
            key: `sim-${item.pageId}-${item.bundleType}-${idx}`,
            kind: 'similar' as const,
            pageId: item.pageId,
            bundleType: item.bundleType,
            providerName: item.providerName,
            categoryName: item.categoryName,
          }))
      : [];

    return { duplicates: dupRows, similars: simRows };
  }, [activeBundleType, pageId, dedupedDuplicates, dedupedSims]);

  const totalCount = relatedRows.duplicates.length + relatedRows.similars.length;

  // The widest page-number label across all rows so we can pin every row's
  // icon-slot to the same width — keeps provider/category text left-aligned
  // regardless of whether a row's page number is 1 or 10000.
  const maxPageLabelLength = useMemo(() => {
    const rows = [...relatedRows.duplicates, ...relatedRows.similars];
    let longest = 1;
    for (const row of rows) {
      const idx =
        activeBundleType === BundleType.LIGHT
          ? getLightBundlePageIndexByPageId(row.pageId)
          : getFullBundlePageIndexByPageId(row.pageId);
      const label = idx >= 0 ? `${idx + 1}` : activeBundleType === BundleType.LIGHT ? 'FB' : '—';
      if (label.length > longest) longest = label.length;
    }
    return longest;
  }, [
    relatedRows.duplicates,
    relatedRows.similars,
    activeBundleType,
    getFullBundlePageIndexByPageId,
    getLightBundlePageIndexByPageId,
  ]);

  const handleMouseEnter = () => {
    if (totalCount === 0) return;
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    openTimeoutRef.current = setTimeout(() => setIsOpen(true), 150);
  };
  const handleMouseLeave = () => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  const handleModalMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };
  const handleModalMouseLeave = () => {
    // Don't close while a row's right-click menu is open — the user is
    // moving off the modal onto that menu.
    if (rowContextMenu) return;
    setIsOpen(false);
  };

  const handleRowClick = async (row: RelatedRow) => {
    const targetBundleType = row.bundleType === 'LIGHT' ? BundleType.LIGHT : BundleType.FULL;
    if (targetBundleType === activeBundleType) {
      const pageIndex = getBundlePageIndexByPageId(row.pageId);
      if (pageIndex !== -1) {
        documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(pageIndex);
      } else {
        globalToastService.show({
          severity: 'info',
          summary: 'Page hidden by filters',
          detail: 'This page is hidden by the filters applied to this bundle.',
        });
      }
      return;
    }

    const broadcast =
      targetBundleType === BundleType.LIGHT
        ? BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE
        : BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE;
    const response = await sendMessageWithResponse(broadcast, { showPageIdInViewport: row.pageId }, 2000).catch(() => {
      const { caseId } = urlParams;
      const targetBundleId = currentBundleMetadata?.otherBundleId;
      if (caseId && targetBundleId) {
        const bundleRoute = targetBundleType === BundleType.LIGHT ? AppRoutes.LightBundle : AppRoutes.FullBundle;
        const newUrl =
          `/${AppRoutes.CaseList}` +
          `/${caseId}` +
          `/${AppRoutes.AiProcessedDocuments}` +
          `/${bundleRoute}` +
          `/${targetBundleId}` +
          `?${AppRoutesSearchParams.pageIdInViewportOnInit}=${row.pageId}`;
        return openNewTab(newUrl);
      }
    });
    if (response instanceof Window) {
      try {
        response.focus();
      } catch {
        // ignore
      }
    }
  };

  // Jump to a specific bundle (Full or Light) for a page id. Mirrors the
  // thumbnail context menu's "View in X Bundle" behaviour: broadcast to the
  // other window, fall back to opening a new tab if it doesn't respond.
  const jumpToBundleForPage = async (targetBundleType: BundleTypeKeys, targetPageId: string) => {
    // Same-bundle: just scroll locally.
    if (targetBundleType === activeBundleType) {
      const idx = getBundlePageIndexByPageId(targetPageId);
      if (idx !== -1) {
        documentReviewPageContext.onScrollToPageIndexInReactVirtualizedViewport(idx);
      } else {
        globalToastService.show({
          severity: 'info',
          summary: 'Page hidden by filters',
          detail: 'This page is hidden by the filters applied to this bundle.',
        });
      }
      return;
    }

    const broadcast =
      targetBundleType === BundleType.LIGHT
        ? BROADCAST_SCROLL_TO_PAGE_ID_IN_LIGHT_BUNDLE
        : BROADCAST_SCROLL_TO_PAGE_ID_IN_FULL_BUNDLE;
    // Choose the *source* metadata for the fallback URL so we point at the
    // correct otherBundleId in each direction.
    const fallbackSource = targetBundleType === BundleType.LIGHT ? fullBundleMetadata : lightBundleMetadata;
    const sourceBundleType = targetBundleType === BundleType.LIGHT ? BundleMetadataType.FULL : BundleMetadataType.LIGHT;
    const bundleRoute = targetBundleType === BundleType.LIGHT ? AppRoutes.LightBundle : AppRoutes.FullBundle;

    const response = await sendMessageWithResponse(broadcast, { showPageIdInViewport: targetPageId }, 2000).catch(
      () => {
        if (
          fallbackSource &&
          fallbackSource.caseId &&
          fallbackSource.otherBundleId &&
          fallbackSource.bundleType === sourceBundleType
        ) {
          const newUrl =
            `/${AppRoutes.CaseList}` +
            `/${fallbackSource.caseId}` +
            `/${AppRoutes.AiProcessedDocuments}` +
            `/${bundleRoute}` +
            `/${fallbackSource.otherBundleId}` +
            `?${AppRoutesSearchParams.pageIdInViewportOnInit}=${targetPageId}`;
          return openNewTab(newUrl);
        }
      },
    );
    if (response instanceof Window) {
      try {
        response.focus();
      } catch {
        // ignore
      }
    }
  };

  // Close the row context menu on outside click or Escape.
  useEffect(() => {
    if (!rowContextMenu) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-related-context-menu="true"]')) return;
      setRowContextMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setRowContextMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [rowContextMenu]);

  // Set of page IDs that exist in the Light bundle (used to show the
  // "Extracted" chip against rows whose underlying page has been extracted).
  // Must sit ABOVE the early return below so the hook count is stable
  // across renders where pageId/activeBundleType flips between missing
  // and present — React matches hooks by call order, not by name.
  const lightBundlePageIds = useMemo(() => {
    const set = new Set<string>();
    (currentDuplicates ?? []).forEach((d) => {
      if (d.bundleType === 'LIGHT') set.add(d.pageId);
    });
    (otherDuplicates ?? []).forEach((d) => {
      if (d.bundleType === 'LIGHT') set.add(d.pageId);
    });
    (currentSimilars ?? []).forEach((s) => {
      if (s.bundleType === 'LIGHT') set.add(s.pageId);
    });
    (otherSimilars ?? []).forEach((s) => {
      if (s.bundleType === 'LIGHT') set.add(s.pageId);
    });
    return set;
  }, [currentDuplicates, otherDuplicates, currentSimilars, otherSimilars]);

  // Only require the basic page context to be present — we always render the
  // button so its position in the rail is stable. The modal shows an
  // "empty state" line when there's nothing related.
  if (!pageId || !activeBundleType) return null;

  const buttonRect = buttonRef.current?.getBoundingClientRect();

  const renderRow = (row: RelatedRow) => {
    // Resolve the page number strictly against the *current* bundle:
    //  - On the Full bundle, use the Full-bundle index.
    //  - On the Light bundle, use the Light-bundle index; if the related
    //    page doesn't exist in the Light bundle, show "FB" so users know
    //    it lives in the Full bundle (we don't cross-look-up any more).
    const pageIndex =
      activeBundleType === BundleType.LIGHT
        ? getLightBundlePageIndexByPageId(row.pageId)
        : getFullBundlePageIndexByPageId(row.pageId);
    const hasPageNumber = pageIndex >= 0;
    const showFullBundleFallback = !hasPageNumber && activeBundleType === BundleType.LIGHT;
    // "Extracted" if:
    //  1. the API tells us the underlying page also exists in the LIGHT bundle
    //     (this covers the initial load), OR
    //  2. the live pageInfoCache says it's in the Light Bundle (this covers
    //     extractions done in the current session — the cache updates
    //     immediately on extract, before any refetch settles).
    const isExtracted =
      row.bundleType === 'LIGHT' ||
      lightBundlePageIds.has(row.pageId) ||
      pageInfoCache[row.pageId]?.inLightBundle === true;

    // Match the confidence colour of the section this row belongs to.
    const accentColor =
      row.kind === 'duplicate'
        ? 'var(--color-fletchers-green-500, #195b53)'
        : 'var(--color-fletchers-green-300, #4e9277)';
    const pageLabel = hasPageNumber ? `${pageIndex + 1}` : showFullBundleFallback ? 'FB' : '—';

    // Keep the page rectangle at proper A4 aspect (1:√2). The digits start
    // at a fixed x anchored on the rectangle's right edge and grow rightward
    // as the page number gets bigger, so single-digit and 5-digit page
    // numbers all begin in the same place.
    const rectW = 10;
    const rectH = 14; // 10 * √2 ≈ 14.14
    // Rectangle sits flush against the SVG's left edge so the icon aligns
    // with the confidence dial in the section header (which also has no
    // internal left padding). Stroke width of 1.3 is accounted for by
    // shifting by half a stroke.
    const rectX = 0.65;
    const rectY = 3;
    const numberFontSize = 12;
    // Fixed anchor point for the digits' LEFT edge — sits a couple of
    // units inside the rectangle's right edge so the digits visibly cut
    // through the corner.
    const numberStartX = rectX + rectW - 4;
    const numberY = rectY + rectH + 1;
    // ViewBox is sized for the LONGEST label in the modal so every row's
    // icon-slot has the same width. Approx digit width ≈ fontSize * 0.55.
    const digitsWidth = maxPageLabelLength * numberFontSize * 0.55;
    const RIGHT_HALO = 3;
    const iconViewWidth = numberStartX + digitsWidth + RIGHT_HALO;
    const iconViewHeight = 22;
    const iconHeight = 24;
    const iconWidth = Math.round((iconViewWidth / iconViewHeight) * iconHeight);

    return (
      <button
        key={row.key}
        type="button"
        onClick={() => handleRowClick(row)}
        onContextMenu={(e) => {
          e.preventDefault();
          setRowContextMenu({
            row,
            isExtracted,
            top: e.clientY,
            left: e.clientX,
          });
        }}
        className={classNames(
          'group -mx-6 flex w-[calc(100%+48px)] cursor-pointer items-center gap-3 px-6 py-2 text-left',
          'hover:bg-fletchers-gray-50 transition-colors',
        )}
      >
        <span
          className="group-hover:text-fletchers-gray-50 shrink-0 text-white"
          aria-label={hasPageNumber ? `Page ${pageIndex + 1}` : 'Unknown page'}
        >
          <svg
            width={iconWidth}
            height={iconHeight}
            viewBox={`0 0 ${iconViewWidth} ${iconViewHeight}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            {/* Single A4 rectangle — proper 1:√2 aspect ratio. Fill/halo
                colours use currentColor so they follow the row's hover
                background (white by default, gray-50 on hover). */}
            <rect
              x={rectX}
              y={rectY}
              width={rectW}
              height={rectH}
              rx="1.5"
              stroke={accentColor}
              strokeWidth="1.3"
              fill="currentColor"
            />
            {/* Page number, halo-outlined so it cuts through the border.
                Left-anchored at a fixed x so every row's digits begin in
                the same place; longer numbers extend further right. */}
            <text
              x={numberStartX}
              y={numberY}
              textAnchor="start"
              fontSize={numberFontSize}
              fontWeight="600"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinejoin="round"
              paintOrder="stroke"
              fill={accentColor}
              style={{ fontFamily: 'inherit' }}
            >
              {pageLabel}
            </text>
          </svg>
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] leading-5 font-normal text-black" title={row.providerName}>
            {row.providerName}
          </span>
          <span
            className="text-fletchers-gray-200 block truncate text-[13px] leading-4 font-normal"
            title={row.categoryName}
          >
            {row.categoryName}
          </span>
        </span>
        {isExtracted && (
          <span
            className="text-fletchers-green-500 flex shrink-0 items-center gap-1 text-[14px] font-semibold"
            title="Extracted to Light Bundle"
            aria-label="Extracted to Light Bundle"
          >
            <CheckCircleIcon className="h-4 w-4" />
            Extracted
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex flex-col">
      <div ref={buttonRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <Button
          text
          icon={(() => {
            const isEmpty = totalCount === 0;
            // Grey when there's nothing to check; a mid Fletchers amber
            // (yellow-600) when there are duplicates/similars — keeps the
            // warm warning colour visible while staying WCAG AA on white.
            const strokeColor = isEmpty ? '#A0A0A0' : 'var(--color-fletchers-yellow-600, #c96f04)';
            const label = totalCount > 99 ? '' : `${totalCount}`;
            return (
              <svg
                width="20"
                height="18"
                viewBox="0 0 20 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="translate-x-[1px]"
                aria-hidden
              >
                {/* bottom (back) A4 rectangle */}
                <rect
                  x="1"
                  y="1"
                  width="10"
                  height="13"
                  rx="1.5"
                  stroke={strokeColor}
                  strokeWidth="1.15"
                  fill="white"
                />
                {/* top (front) A4 rectangle, offset to bottom-right */}
                <rect
                  x="5"
                  y="4"
                  width="10"
                  height="13"
                  rx="1.5"
                  stroke={strokeColor}
                  strokeWidth="1.15"
                  fill="white"
                />
                {/* Number rendered twice: first a thick white stroke around it (erases
                    the rectangle borders behind so the digits sit in white space),
                    then the same digits in the rectangle colour on top. */}
                <text
                  x="14"
                  y="17"
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  stroke="white"
                  strokeWidth="3.5"
                  strokeLinejoin="round"
                  paintOrder="stroke"
                  fill={strokeColor}
                  style={{ fontFamily: 'inherit' }}
                >
                  {label}
                </text>
              </svg>
            );
          })()}
          aria-label="Related pages"
          tooltip={totalCount === 0 ? 'No duplicates detected' : undefined}
          tooltipOptions={railTooltipOptions}
          className={tooltipButtonBase}
          pt={hideLabel}
        />

        {isOpen && buttonRect && (
          <RelatedPagesModal
            buttonRect={buttonRect}
            totalCount={totalCount}
            duplicateRows={relatedRows.duplicates}
            similarRows={relatedRows.similars}
            onMouseEnter={handleModalMouseEnter}
            onMouseLeave={handleModalMouseLeave}
            renderRow={renderRow}
          />
        )}
      </div>

      {activeBundleType !== BundleType.LIGHT && (
        <>
          {/* Divider matching the zoom/rotate/nav rail dividers in
              ZoomAndNavigatePdfPanel — same colour so both buttons read as one
              bordered rail. */}
          <div className="bg-fletchers-gray-110 h-px" />
          <Button
            text
            icon={
              <svg
                width="20"
                height="18"
                viewBox="0 0 20 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="translate-x-[1px]"
                aria-hidden
              >
                <rect x="3" y="1" width="10" height="13" rx="1.5" stroke="#09090B" strokeWidth="1.15" fill="white" />
                <text
                  x="14"
                  y="17"
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="700"
                  stroke="white"
                  strokeWidth="3.5"
                  strokeLinejoin="round"
                  paintOrder="stroke"
                  fill="#09090B"
                  style={{ fontFamily: 'inherit' }}
                >
                  ?
                </text>
              </svg>
            }
            aria-label="Something's missing"
            tooltip="Something's missing"
            tooltipOptions={railTooltipOptions}
            className={tooltipButtonBase}
            pt={hideLabel}
            onClick={() => {
              if (!pageId || !activeBundleType) return;
              dispatch(setBundleRetrieveBasePageId({ key: activeBundleType, data: pageId }));
              setIsRetrievePagesDialogVisible(true);
            }}
          />
        </>
      )}
      {isRetrievePagesDialogVisible && <RetrievePagesDialog setVisible={setIsRetrievePagesDialogVisible} />}

      {rowContextMenu &&
        createPortal(
          <div
            data-related-context-menu="true"
            className="border-fletchers-gray-110 fixed z-[1100] min-w-[200px] overflow-hidden rounded-[6px] border bg-white shadow-[0px_4px_12px_0px_#0000001f]"
            style={{ top: rowContextMenu.top, left: rowContextMenu.left }}
          >
            <button
              type="button"
              className="hover:bg-fletchers-gray-50 block w-full cursor-pointer px-4 py-2 text-left text-[14px] font-semibold text-black"
              onClick={() => {
                void jumpToBundleForPage(BundleType.FULL, rowContextMenu.row.pageId);
                setRowContextMenu(null);
              }}
            >
              View in Full Bundle
            </button>
            <button
              type="button"
              disabled={!rowContextMenu.isExtracted}
              className={classNames(
                'block w-full px-4 py-2 text-left text-[14px] font-semibold',
                rowContextMenu.isExtracted
                  ? 'hover:bg-fletchers-gray-50 cursor-pointer text-black'
                  : 'text-fletchers-gray-150 cursor-not-allowed',
              )}
              onClick={() => {
                if (!rowContextMenu.isExtracted) return;
                void jumpToBundleForPage(BundleType.LIGHT, rowContextMenu.row.pageId);
                setRowContextMenu(null);
              }}
            >
              View in Light Bundle
            </button>
          </div>,
          document.body,
        )}
    </div>
  );
};
